/**
 * Central Analytics Event Bus
 * 
 * Manages all tracking plugins (Meta, TikTok, GTM, etc.) in isolated sandboxes.
 * Ensures consistent event payloads, automatic deduplication IDs, and robust
 * error protection so that an error in any analytics vendor never crashes the app.
 */

import { AnalyticsPlugin, AnalyticsEventPayload, AnalyticsItem } from './types';

export class AnalyticsBus {
  private plugins: Map<string, AnalyticsPlugin> = new Map();

  /**
   * Register a tracking plugin (e.g. FacebookPlugin, TikTokPlugin, GtmPlugin)
   */
  registerPlugin(plugin: AnalyticsPlugin): this {
    this.plugins.set(plugin.name, plugin);
    return this;
  }

  /**
   * Remove a plugin by name
   */
  unregisterPlugin(pluginName: string): boolean {
    return this.plugins.delete(pluginName);
  }

  /**
   * Get a plugin by name
   */
  getPlugin(pluginName: string): AnalyticsPlugin | undefined {
    return this.plugins.get(pluginName);
  }

  /**
   * List all registered plugin names
   */
  getRegisteredPluginNames(): string[] {
    return Array.from(this.plugins.keys());
  }

  /**
   * Core dispatch method.
   * Generates deterministic deduplication keys and dispatches to all
   * registered plugins in independent try-catch sandboxes.
   */
  async track(eventName: string, data?: Partial<AnalyticsEventPayload>): Promise<void> {
    if (typeof window === 'undefined') return;

    // 1. Guard against firing empty ViewContent without content_id
    // This permanently prevents the TikTok / Meta "Content ID is missing" warning.
    const contentIdsList = data?.content_ids;
    const contentsList = data?.contents;
    const hasContentId = Boolean(
      data?.content_id ||
      (Array.isArray(contentIdsList) && contentIdsList.length > 0) ||
      (Array.isArray(contentsList) && contentsList.length > 0)
    );

    if (eventName === 'ViewContent' && !hasContentId) {
      // PageView already tracks generic landing visits.
      return;
    }

    // 2. Deterministic event_id for client-server CAPI & Events API deduplication
    const eventId = data?.order_id
      ? `Purchase_${data.order_id}`
      : (data?.event_id || `${eventName}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`);

    const payload: AnalyticsEventPayload = {
      ...data,
      event_id: eventId,
    };

    // 3. Sandboxed parallel dispatch across all registered plugins
    const dispatchPromises = Array.from(this.plugins.values()).map(async (plugin) => {
      try {
        await Promise.resolve(plugin.track(eventName, payload));
      } catch (error) {
        console.error(`[Analytics Bus] Plugin "${plugin.name}" error on event "${eventName}":`, error);
      }
    });

    await Promise.allSettled(dispatchPromises);
  }

  // ==========================================
  // E-commerce Convenience Helpers
  // ==========================================

  /**
   * View a single product or category view
   */
  async viewContent(product: {
    id: string | number;
    name: string;
    price: number;
    category?: string;
    sku?: string;
  }): Promise<void> {
    const stringId = String(product.id);
    return this.track('ViewContent', {
      content_id: stringId,
      content_ids: [stringId],
      content_name: product.name,
      content_type: 'product',
      value: product.price,
      currency: 'BDT',
      sku: product.sku,
      contents: [
        {
          content_id: stringId,
          content_name: product.name,
          content_type: 'product',
          price: product.price,
          quantity: 1,
          category: product.category,
          sku: product.sku,
        },
      ],
    });
  }

  /**
   * Add a product to cart
   */
  async addToCart(product: {
    id: string | number;
    name: string;
    price: number;
    quantity?: number;
    sku?: string;
  }): Promise<void> {
    const qty = product.quantity || 1;
    const stringId = String(product.id);
    return this.track('AddToCart', {
      content_id: stringId,
      content_ids: [stringId],
      content_name: product.name,
      content_type: 'product',
      value: product.price * qty,
      currency: 'BDT',
      quantity: qty,
      sku: product.sku,
      contents: [
        {
          content_id: stringId,
          content_name: product.name,
          content_type: 'product',
          price: product.price,
          quantity: qty,
          sku: product.sku,
        },
      ],
    });
  }

  /**
   * Initiate Checkout flow
   */
  async initiateCheckout(items: AnalyticsItem[], totalValue: number): Promise<void> {
    const contentIds = items.map((i) => String(i.content_id));
    return this.track('InitiateCheckout', {
      content_id: contentIds[0] || '',
      content_ids: contentIds,
      content_type: 'product',
      contents: items.map((i) => ({
        content_id: String(i.content_id),
        content_name: i.content_name || '',
        content_type: 'product',
        price: i.price || 0,
        quantity: i.quantity || 1,
      })),
      num_items: items.reduce((sum, i) => sum + (i.quantity || 1), 0),
      value: totalValue,
      currency: 'BDT',
    });
  }

  /**
   * Track successful order purchase
   */
  async purchase(order: {
    orderId: string;
    totalAmount: number;
    items: Array<{
      id: string | number;
      name?: string;
      price?: number;
      quantity?: number;
      sku?: string;
    }>;
    currency?: string;
  }): Promise<void> {
    const contentIds = order.items.map((i) => String(i.id));
    return this.track('Purchase', {
      order_id: order.orderId,
      content_id: contentIds[0] || '',
      content_ids: contentIds,
      content_type: 'product',
      contents: order.items.map((i) => ({
        content_id: String(i.id),
        content_name: i.name || '',
        content_type: 'product',
        price: i.price || 0,
        quantity: i.quantity || 1,
        sku: i.sku,
      })),
      num_items: order.items.reduce((sum, i) => sum + (i.quantity || 1), 0),
      value: order.totalAmount,
      currency: order.currency || 'BDT',
    });
  }
}
